import {
  FC, useEffect, useState, useCallback,
} from 'react';

import { OrderRequestDto, OrderResponseDto } from 'shared/api';
import { OrderListItem } from 'entities/order';
import { showConfirm } from 'shared/utils';
import { EmptyState } from 'shared/ui';
import { useOrders } from '../hooks/useOrders';
import { OrderTable, OrderForm, CompleteOrderModal } from '../components';

import styles from './OrdersPage.module.scss';

export const OrdersPage: FC = () => {
  const {
    orders,
    loadOrders,
    updateExistingOrder,
    deleteOrderById,
    completeOrderById,
    downloadAttachment,
    loadOrderDetail,
  } = useOrders();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState<OrderRequestDto | null>(null);
  const [editingOrderId, setEditingOrderId] = useState<number | null>(null);
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
  const [completingOrder, setCompletingOrder] = useState<OrderListItem | null>(null);

  useEffect(() => {
    loadOrders();
  }, []);

  const mapDetailToForm = useCallback((detail?: OrderResponseDto | null): OrderRequestDto | null => {
    if (!detail) return null;
    return {
      engineId: detail.engineId || 0,
      workType: detail.workType as OrderRequestDto.workType,
      status: detail.status as OrderRequestDto.status,
      comment: detail.comment,
    };
  }, []);

  const handleEdit = useCallback(async (order: OrderListItem) => {
    setEditingOrderId(order.id || null);
    setIsModalOpen(true);
    setEditingOrder(null);
    if (order.id) {
      const res = await loadOrderDetail(order.id);
      if (res.success) {
        setEditingOrder(mapDetailToForm(res.detail));
      }
    }
  }, [loadOrderDetail, mapDetailToForm]);

  const handleDelete = useCallback(async (id: number) => {
    const confirmed = await showConfirm({ title: 'Вы уверены, что хотите удалить этот заказ?' });
    if (confirmed) {
      await deleteOrderById(id);
    }
  }, [deleteOrderById]);

  const handleSubmit = useCallback(async (data: OrderRequestDto) => {
    await updateExistingOrder({
      id: editingOrderId,
      data,
      onSuccess: () => {
        setIsModalOpen(false);
        setEditingOrder(null);
        setEditingOrderId(null);
      },
    });
  }, [editingOrderId, updateExistingOrder]);

  const handleCancel = useCallback(() => {
    setIsModalOpen(false);
    setEditingOrder(null);
    setEditingOrderId(null);
  }, []);

  const handleComplete = useCallback((order: OrderListItem) => {
    setCompletingOrder(order);
    setIsCompleteModalOpen(true);
  }, []);

  const handleCompleteConfirm = useCallback(async (reportFileUuid: string) => {
    if (completingOrder?.id) {
      await completeOrderById(completingOrder.id, reportFileUuid);
    }
  }, [completingOrder, completeOrderById]);

  const handleCompleteCancel = useCallback(() => {
    setIsCompleteModalOpen(false);
    setCompletingOrder(null);
  }, []);

  const handleDownload = useCallback(async (attachmentUuid: string, filename?: string) => {
    await downloadAttachment(attachmentUuid, filename);
  }, [downloadAttachment]);

  return (
    <div className={styles.ordersPage}>
      <div className={styles.header}>
        <h1>Заявки на обслуживание</h1>
      </div>

      {orders.length === 0 ? (
        <EmptyState
          message="Заявки отсутствуют"
          icon="📋"
        />
      ) : (
        <OrderTable
          orders={orders}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onComplete={handleComplete}
          onDownload={handleDownload}
        />
      )}

      {isModalOpen && (
        <OrderForm
          editingOrder={editingOrder}
          isEditing={!!editingOrderId}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      )}

      <CompleteOrderModal
        order={completingOrder}
        isOpen={isCompleteModalOpen}
        onClose={handleCompleteCancel}
        onConfirm={handleCompleteConfirm}
      />
    </div>
  );
};
